/**
 * A complete storefront palette from three brand colors.
 *
 * Staff know their primary, maybe a secondary, and whether the store is dark
 * or light. They do not know what `--muted-foreground` should be. This derives
 * every one of the 33 tokens from those seeds so the per-token editor becomes a
 * place to fine-tune rather than the place a theme has to be built.
 *
 * The method, in OKLCH throughout:
 *  - the *mode* (dark or light) comes from the background's lightness;
 *  - surfaces (card, sidebar, muted, border) sit on a lightness ladder above
 *    (dark) or below (light) the background, tinted with its hue. The ladder's
 *    offsets are read off the stock palette, so the stock seeds reproduce the
 *    stock palette — see storefrontPalette.test.ts;
 *  - every `*-foreground` is *chosen by contrast* between the palette's light
 *    and dark text colors, never assumed;
 *  - a blank secondary is derived from the primary by a color-harmony rule
 *    (triadic by default; "muted" reproduces the presets' quiet secondary);
 *  - chart series are spread around the wheel from the primary's hue.
 *
 * Output is `#rrggbb` for every token because that is what the dashboard's
 * color fields and the stored theme already speak. Nothing about the seeds is
 * persisted; the result is just a bulk fill of the same fields, exactly like a
 * preset. Seeds pass through unchanged, so feeding a generated palette's
 * primary/secondary/background back in reproduces it.
 */

import {
  formatHex,
  hexContrast,
  hexToOklch,
  normalizeHue,
  type Oklch,
  parseHex,
  rgbToOklch,
  toGamutHex,
} from "./color.js";
import type { StorefrontTokenKey } from "./storefrontTokens.js";

export type SecondaryHarmony = "muted" | "complementary" | "analogous" | "triadic" | "split";

/** The harmony used when a blank secondary comes with no explicit rule. */
export const DEFAULT_SECONDARY_HARMONY: SecondaryHarmony = "triadic";

/** How a blank secondary is derived from the primary, in the order the picker lists them. */
export const SECONDARY_HARMONIES: readonly {
  id: SecondaryHarmony;
  label: string;
  description: string;
}[] = [
  { id: "triadic", label: "Triadic", description: "A third of the wheel from the primary." },
  {
    id: "complementary",
    label: "Complementary",
    description: "Opposite the primary on the wheel.",
  },
  { id: "analogous", label: "Analogous", description: "A neighbour of the primary, 30° away." },
  {
    id: "split",
    label: "Split-complementary",
    description: "Just off the opposite hue, 150° away.",
  },
  { id: "muted", label: "Muted surface", description: "A quiet panel color, like the presets." },
];

export interface PaletteSeeds {
  primary: string;
  /** Blank or omitted → derived from the primary via `harmony`. */
  secondary?: string;
  background: string;
  /** Only consulted when `secondary` is blank. Defaults to DEFAULT_SECONDARY_HARMONY. */
  harmony?: SecondaryHarmony;
}

export type StorefrontPalette = Record<StorefrontTokenKey, string>;

type Mode = "dark" | "light";

/** Dark or light, from the background's lightness — null when it isn't hex. */
export function inferColorScheme(background: string): Mode | null {
  const oklch = hexToOklch(background);
  return oklch ? modeOf(oklch) : null;
}

function modeOf(background: Oklch): Mode {
  return background.L < 0.5 ? "dark" : "light";
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

/** Smallest angle between two hues, 0..180. */
function hueDistance(a: number, b: number): number {
  const d = Math.abs(normalizeHue(a) - normalizeHue(b));
  return Math.min(d, 360 - d);
}

function seed(name: string, value: string): { hex: string; oklch: Oklch } {
  const rgb = parseHex(value);
  if (!rgb) throw new Error(`${name} must be a hex color, got ${JSON.stringify(value)}`);
  // Normalised so "#FFF" and "#ffffff" are the same seed and compare equal on output.
  return { hex: formatHex(rgb), oklch: rgbToOklch(rgb) };
}

/**
 * Nudges a text color's lightness away from its surface until it clears `min`.
 * Bounded so a surface no text can read against (mid-grey) terminates at
 * white or black rather than looping.
 */
function ensureContrast(text: Oklch, surface: string, min: number, mode: Mode): string {
  const current = { ...text };
  for (let i = 0; i < 25; i++) {
    const hex = toGamutHex(current);
    if (hexContrast(hex, surface) >= min) return hex;
    current.L = clamp(current.L + (mode === "dark" ? 0.02 : -0.02), 0, 1);
  }
  return toGamutHex(current);
}

// The harmony hue offsets, applied to the primary's hue.
const HARMONY_OFFSET: Record<Exclude<SecondaryHarmony, "muted">, number> = {
  complementary: 180,
  analogous: 30,
  triadic: 120,
  split: 150,
};

/** The hue the destructive tokens use; the analogous rule steers clear of it. */
const DESTRUCTIVE_HUE = 25;

export function generateStorefrontPalette(seeds: PaletteSeeds): StorefrontPalette {
  const primary = seed("primary", seeds.primary);
  const background = seed("background", seeds.background);
  const mode = modeOf(background.oklch);
  const dark = mode === "dark";
  const bg = background.oklch;
  const p = primary.oklch;

  // A neutral background has no hue to tint the ladder with; borrow the brand's.
  const hue = bg.C >= 0.004 ? bg.h : p.h;
  // Surface chroma tracks the background's, capped so a vivid background seed
  // doesn't turn every panel neon. The background itself is left as given.
  const tint = (k: number) => clamp(bg.C * k, 0.004, 0.05);

  // ---- surface ladder --------------------------------------------------------
  const card: Oklch = dark
    ? { L: bg.L + 0.04, C: tint(1.3), h: hue }
    : { L: Math.min(1, bg.L + 0.02), C: tint(0.5), h: hue };
  const sidebar: Oklch = dark
    ? { L: bg.L + 0.025, C: tint(1.15), h: hue }
    : { L: Math.min(1, bg.L + 0.01), C: tint(0.75), h: hue };
  const muted: Oklch = dark
    ? { L: bg.L + 0.11, C: tint(1.8), h: hue }
    : { L: bg.L - 0.03, C: tint(1.5), h: hue };
  const border: Oklch = dark
    ? { L: bg.L + 0.12, C: tint(1.8), h: hue }
    : { L: bg.L - 0.08, C: tint(2), h: hue };

  const cardHex = toGamutHex(card);
  const sidebarHex = toGamutHex(sidebar);
  const mutedHex = toGamutHex(muted);
  const borderHex = toGamutHex(border);

  // ---- text ------------------------------------------------------------------
  const foregroundHex = dark
    ? ensureContrast({ L: Math.max(0.93, bg.L + 0.55), C: 0.01, h: hue }, background.hex, 4.5, mode)
    : ensureContrast(
        { L: Math.min(0.14, bg.L - 0.55), C: 0.02, h: hue },
        background.hex,
        4.5,
        mode,
      );
  const mutedForegroundHex = dark
    ? ensureContrast({ L: Math.max(0.66, bg.L + 0.35), C: 0.03, h: hue }, mutedHex, 4.5, mode)
    : ensureContrast({ L: Math.min(0.53, bg.L - 0.35), C: 0.03, h: hue }, mutedHex, 4.5, mode);

  // The two text colors any surface gets to choose from. In dark mode the dark
  // one is the background itself — the stock palette's primary-foreground rule —
  // and in light mode the light one is a near-white; either way text stays
  // tinted with the page rather than dropping to pure black or white.
  const lightText = dark ? foregroundHex : toGamutHex({ L: 0.93, C: 0.01, h: hue });
  const darkText = dark ? background.hex : foregroundHex;
  const pick = (surface: string): string => {
    const light = hexContrast(lightText, surface);
    const darkRatio = hexContrast(darkText, surface);
    if (light === darkRatio) return foregroundHex;
    return light > darkRatio ? lightText : darkText;
  };

  // ---- secondary -------------------------------------------------------------
  let secondaryHex: string;
  if (seeds.secondary?.trim()) {
    secondaryHex = seed("secondary", seeds.secondary).hex;
  } else {
    const harmony = seeds.harmony ?? DEFAULT_SECONDARY_HARMONY;
    if (harmony === "muted") {
      secondaryHex = mutedHex;
    } else {
      let offset = HARMONY_OFFSET[harmony];
      // An analogous secondary that lands on the error red would make every
      // secondary button read as destructive; take the other neighbour.
      if (harmony === "analogous" && hueDistance(p.h + offset, DESTRUCTIVE_HUE) < 20) {
        offset = -offset;
      }
      // Lit as a button surface: light enough (dark mode) or deep enough (light
      // mode) that the contrast-picked text on it clears AA comfortably.
      secondaryHex = toGamutHex({
        L: dark ? clamp(p.L, 0.62, 0.8) : clamp(p.L, 0.42, 0.52),
        C: Math.min(0.8 * p.C, dark ? 0.13 : 0.14),
        h: normalizeHue(p.h + offset),
      });
    }
  }
  // Re-read from hex so a derived secondary and the same hex typed in produce
  // identical downstream tokens — the idempotence the tests pin.
  const s = seed("secondary", secondaryHex).oklch;

  // ---- accent ----------------------------------------------------------------
  // shadcn uses accent as the hover fill under ghost buttons and menu items, so
  // it has to be a surface step, not a fourth color: the muted lightness with a
  // faint wash of the secondary's hue. With the muted secondary it *is* muted.
  const accentHex =
    secondaryHex === mutedHex
      ? mutedHex
      : toGamutHex({
          L: muted.L,
          C: clamp(0.5 * s.C, muted.C, 0.06),
          h: s.C >= 0.02 ? s.h : hue,
        });

  // ---- destructive -----------------------------------------------------------
  const destructiveHex = toGamutHex(
    dark ? { L: 0.637, C: 0.237, h: 25.331 } : { L: 0.58, C: 0.22, h: 25 },
  );
  // White-on-red is the convention shoppers know; keep it whenever it reads at
  // all (button text is bold), rather than flipping to dark text on a light red.
  // The stock near-white, untinted: error text should look the same in every store.
  const destructiveText = toGamutHex({ L: 0.98, C: 0.005, h: 250 });
  const destructiveForegroundHex =
    hexContrast(destructiveText, destructiveHex) >= 3 ? destructiveText : pick(destructiveHex);

  // ---- charts ----------------------------------------------------------------
  // Series at one reading lightness, set off from the background by a fixed
  // step so a light-but-not-white page (or a dark-but-not-black one) still gets
  // marks that clear 3:1; ensureContrast covers the hues that read darker.
  const chartL = dark ? Math.min(0.95, Math.max(0.75, bg.L + 0.45)) : Math.min(0.55, bg.L - 0.35);
  const chartAt = (h: number) =>
    ensureContrast({ L: chartL, C: 0.15, h: normalizeHue(h) }, background.hex, 3, mode);
  // Four slots evenly spaced from the primary. A chromatic secondary takes the
  // chart-2 seat and evicts the slot it sits nearest, keeping wheel order, so
  // every series stays at least 36° from its neighbours.
  const slot1 = chartAt(p.h + 72);
  const slot2 = chartAt(p.h + 144);
  const slot3 = chartAt(p.h + 216);
  const slot4 = chartAt(p.h + 288);
  let chart2 = slot1;
  let rest: [string, string, string] = [slot2, slot3, slot4];
  if (s.C >= 0.08) {
    chart2 = chartAt(s.h);
    // Slots sit at 72°·k from the primary, so the nearest is round(Δ/72),
    // pinned to 1..4 because k=0 is the primary's own seat.
    const occupied = clamp(Math.round(normalizeHue(s.h - p.h) / 72), 1, 4);
    rest =
      occupied === 1
        ? [slot2, slot3, slot4]
        : occupied === 2
          ? [slot1, slot3, slot4]
          : occupied === 3
            ? [slot1, slot2, slot4]
            : [slot1, slot2, slot3];
  }

  const primaryForegroundHex = pick(primary.hex);
  const accentForegroundHex = pick(accentHex);

  return {
    background: background.hex,
    foreground: foregroundHex,
    border: borderHex,
    input: borderHex,
    ring: primary.hex,
    card: cardHex,
    "card-foreground": pick(cardHex),
    popover: cardHex,
    "popover-foreground": pick(cardHex),
    muted: mutedHex,
    "muted-foreground": mutedForegroundHex,
    primary: primary.hex,
    "primary-foreground": primaryForegroundHex,
    secondary: secondaryHex,
    "secondary-foreground": pick(secondaryHex),
    accent: accentHex,
    "accent-foreground": accentForegroundHex,
    destructive: destructiveHex,
    "destructive-foreground": destructiveForegroundHex,
    "chart-1": primary.hex,
    "chart-2": chart2,
    "chart-3": rest[0],
    "chart-4": rest[1],
    "chart-5": rest[2],
    sidebar: sidebarHex,
    "sidebar-foreground": foregroundHex,
    "sidebar-primary": primary.hex,
    "sidebar-primary-foreground": primaryForegroundHex,
    "sidebar-accent": accentHex,
    "sidebar-accent-foreground": accentForegroundHex,
    "sidebar-border": borderHex,
    "sidebar-ring": primary.hex,
  };
}

export interface PaletteContrastRow {
  fg: StorefrontTokenKey;
  bg: StorefrontTokenKey;
  ratio: number;
  /** 4.5 for body text (WCAG AA), 3 for bold button labels and non-text chart marks. */
  min: 3 | 4.5;
  pass: boolean;
}

const CONTRAST_PAIRS: readonly [StorefrontTokenKey, StorefrontTokenKey, 3 | 4.5][] = [
  ["foreground", "background", 4.5],
  ["card-foreground", "card", 4.5],
  ["popover-foreground", "popover", 4.5],
  ["muted-foreground", "muted", 4.5],
  ["accent-foreground", "accent", 4.5],
  ["primary-foreground", "primary", 3],
  ["secondary-foreground", "secondary", 3],
  ["destructive-foreground", "destructive", 3],
  ["chart-1", "background", 3],
  ["chart-2", "background", 3],
  ["chart-3", "background", 3],
  ["chart-4", "background", 3],
  ["chart-5", "background", 3],
];

/**
 * WCAG contrast for every text-on-surface pair a storefront actually renders.
 * Works on any complete palette, generated or hand-edited, so the dashboard can
 * flag a token someone typed over as readily as one the generator produced.
 * Non-hex values yield NaN and a failing row rather than a silent pass.
 */
export function paletteContrastReport(tokens: StorefrontPalette): PaletteContrastRow[] {
  return CONTRAST_PAIRS.map(([fg, bg, min]) => {
    const ratio = hexContrast(tokens[fg], tokens[bg]);
    return { fg, bg, ratio, min, pass: ratio >= min };
  });
}
